import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';
import { formatApiDate, getTodayDateInputValue } from '../utils/date';

const formatearMonto = (monto) => parseFloat(monto || 0).toFixed(2);

const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return formatApiDate(fecha);
};

const PlanesAdminPage = () => {
    const navigate = useNavigate();
    const [planes, setPlanes] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalNuevoPlan, setModalNuevoPlan] = useState(false);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [deudaCliente, setDeudaCliente] = useState(null);
    const [interes, setInteres] = useState('');
    const [cuotas, setCuotas] = useState(2);
    const [planConfig, setPlanConfig] = useState({ interesPorcentaje: 0, maxCuotas: 5 });
    const [creandoPlan, setCreandoPlan] = useState(false);
    const [modalConfirmRevertir, setModalConfirmRevertir] = useState({ isOpen: false, grupoId: null, clienteNombre: '' });
    const [revirtiendo, setRevirtiendo] = useState(false);
    const [modalAdelanto, setModalAdelanto] = useState({ isOpen: false, plan: null, preview: null, loading: false });
    const [modoAdelanto, setModoAdelanto] = useState('cuotas');
    const [cantidadAdelanto, setCantidadAdelanto] = useState(1);
    const [fechaAdelanto, setFechaAdelanto] = useState(getTodayDateInputValue());
    const [metodoAdelanto, setMetodoAdelanto] = useState('efectivo');
    const [observacionesAdelanto, setObservacionesAdelanto] = useState('');
    const [registrandoAdelanto, setRegistrandoAdelanto] = useState(false);
    const [modal, setModal] = useState({
        isOpen: false,
        type: 'info',
        title: '',
        message: '',
    });

    useEffect(() => {
        cargarPlanes();
        cargarClientes();
        cargarConfigPlanPago();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cargarPlanes = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/tarifario/planes-pago/listado');
            setPlanes(response.data || []);
        } catch (error) {
            console.error('Error al cargar planes:', error);
            showModal('error', 'Error', 'No se pudieron cargar los planes de pago');
        } finally {
            setLoading(false);
        }
    };

    const cargarClientes = async () => {
        try {
            const response = await axios.get('/usuarios/clientes');
            setClientes(response.data || []);
        } catch (error) {
            console.error('Error al cargar clientes:', error);
        }
    };

    const cargarConfigPlanPago = async () => {
        try {
            const response = await axios.get('/tarifario/plan-pago/config');
            setPlanConfig(response.data || { interesPorcentaje: 0, maxCuotas: 5 });
            setInteres(response.data?.interesPorcentaje?.toString() || '0');
        } catch (error) {
            console.error('Error al cargar config:', error);
        }
    };

    const showModal = (type, title, message) => {
        setModal({ isOpen: true, type, title, message });
    };

    const closeModal = () => {
        setModal({ isOpen: false, type: 'info', title: '', message: '' });
    };

    const abrirModalNuevoPlan = () => {
        setClienteSeleccionado(null);
        setDeudaCliente(null);
        setInteres(planConfig.interesPorcentaje?.toString() || '0');
        setCuotas(2);
        setModalNuevoPlan(true);
    };

    const cerrarModalNuevoPlan = () => {
        setModalNuevoPlan(false);
        setClienteSeleccionado(null);
        setDeudaCliente(null);
    };

    const handleClienteChange = async (e) => {
        const clienteId = e.target.value;
        if (!clienteId) {
            setClienteSeleccionado(null);
            setDeudaCliente(null);
            return;
        }

        const cliente = clientes.find(c => c.id === Number(clienteId));
        setClienteSeleccionado(cliente);

        // Cargar deuda del cliente
        try {
            const response = await axios.get(`/tarifario/deuda-total/${clienteId}`);
            setDeudaCliente(response.data || null);
        } catch (error) {
            console.error('Error al cargar deuda:', error);
            setDeudaCliente(null);
        }
    };

    const crearPlan = async () => {
        if (!clienteSeleccionado) {
            showModal('error', 'Error', 'Seleccione un cliente');
            return;
        }

        if (!deudaCliente || !deudaCliente.boletas || deudaCliente.boletas.length === 0) {
            showModal('error', 'Error', 'El cliente no tiene boletas adeudadas');
            return;
        }

        setCreandoPlan(true);
        try {
            const interesNumerico = parseFloat(interes);
            await axios.post('/tarifario/plan-pago-consolidado', {
                usuarioId: clienteSeleccionado.id,
                cantidadCuotas: cuotas,
                interesPorcentajePersonalizado: interesNumerico,
            });

            showModal('success', 'Éxito', 'Plan de pago creado correctamente');
            cerrarModalNuevoPlan();
            cargarPlanes();
        } catch (error) {
            console.error('Error al crear plan:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Error desconocido';
            showModal('error', 'Error', `No se pudo crear el plan: ${errorMsg}`);
        } finally {
            setCreandoPlan(false);
        }
    };

    const abrirConfirmRevertir = (plan) => {
        setModalConfirmRevertir({ isOpen: true, grupoId: plan.grupoId, clienteNombre: plan.cliente.nombre });
    };

    const confirmarRevertir = async () => {
        const { grupoId } = modalConfirmRevertir;
        setModalConfirmRevertir({ isOpen: false, grupoId: null, clienteNombre: '' });
        setRevirtiendo(true);
        try {
            await axios.delete(`/tarifario/plan-pago/${grupoId}`);
            showModal('success', 'Plan revertido', 'El plan de pago fue revertido. Las boletas volvieron a estado pendiente.');
            cargarPlanes();
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Error desconocido';
            showModal('error', 'Error', `No se pudo revertir el plan: ${errorMsg}`);
        } finally {
            setRevirtiendo(false);
        }
    };

    const cerrarModalAdelanto = () => {
        setModalAdelanto({ isOpen: false, plan: null, preview: null, loading: false });
        setModoAdelanto('cuotas');
        setCantidadAdelanto(1);
        setFechaAdelanto(getTodayDateInputValue());
        setMetodoAdelanto('efectivo');
        setObservacionesAdelanto('');
    };

    const abrirModalAdelanto = async (plan) => {
        setModalAdelanto({ isOpen: true, plan, preview: null, loading: true });
        setModoAdelanto('cuotas');
        setCantidadAdelanto(1);
        setFechaAdelanto(getTodayDateInputValue());
        setMetodoAdelanto('efectivo');
        setObservacionesAdelanto('');

        try {
            const response = await axios.get(`/tarifario/plan-pago/${plan.grupoId}/adelanto-preview`);
            const preview = response.data;
            const cuotasPendientes = preview?.resumen?.cuotasPendientes || 0;
            setCantidadAdelanto(cuotasPendientes > 0 ? 1 : 0);
            setModalAdelanto({ isOpen: true, plan, preview, loading: false });
        } catch (error) {
            setModalAdelanto({ isOpen: false, plan: null, preview: null, loading: false });
            const errorMsg = error.response?.data?.message || error.message || 'Error desconocido';
            showModal('error', 'No se pudo cargar el plan', errorMsg);
        }
    };

    const confirmarAdelanto = async () => {
        const { plan, preview } = modalAdelanto;
        if (!plan || !preview) return;

        const cuotasPendientes = preview.resumen?.cuotasPendientes || 0;
        if (cuotasPendientes < 1) {
            showModal('warning', 'Sin cuotas pendientes', 'El plan no tiene cuotas pendientes para adelantar.');
            return;
        }

        const cantidad = modoAdelanto === 'total' ? cuotasPendientes : Number(cantidadAdelanto);
        if (!Number.isInteger(cantidad) || cantidad < 1 || cantidad > cuotasPendientes) {
            showModal('warning', 'Cantidad inválida', 'Seleccione una cantidad válida de cuotas pendientes.');
            return;
        }

        setRegistrandoAdelanto(true);
        try {
            const response = await axios.post(`/tarifario/plan-pago/${plan.grupoId}/adelantar`, {
                cantidadCuotas: cantidad,
                pagarTotal: modoAdelanto === 'total',
                fechaPago: fechaAdelanto,
                metodoPago: metodoAdelanto,
                observaciones: observacionesAdelanto,
            });

            const resultado = response.data;
            cerrarModalAdelanto();
            await cargarPlanes();
            showModal(
                'success',
                resultado.planFinalizado ? 'Plan cancelado' : 'Cuotas adelantadas',
                `Se registró un pago de $${formatearMonto(resultado.adelanto?.monto)} por ${resultado.adelanto?.cantidadCuotas || cantidad} cuota(s).\n${resultado.planFinalizado ? 'El plan quedó finalizado.' : 'El plan conserva sus cuotas pendientes en el calendario original.'}`,
            );
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Error desconocido';
            showModal('error', 'No se pudo registrar el adelanto', errorMsg);
        } finally {
            setRegistrandoAdelanto(false);
        }
    };

    // Cálculos del plan
    const montoBase = deudaCliente?.total || 0;
    const interesPorc = parseFloat(interes) || 0;
    const montoInteres = (montoBase * interesPorc) / 100;
    const montoTotal = montoBase + montoInteres;
    const montoCuota = cuotas > 0 ? Math.floor((montoTotal / cuotas) * 100) / 100 : 0;
    const montoCuotaUltima = montoTotal - (montoCuota * (cuotas - 1));
    const cuotasPendientesAdelanto = modalAdelanto.preview?.cuotas?.filter((cuota) => cuota.estado === 'pendiente') || [];
    const cantidadSeleccionadaAdelanto = modoAdelanto === 'total'
        ? cuotasPendientesAdelanto.length
        : Math.min(Number(cantidadAdelanto) || 0, cuotasPendientesAdelanto.length);
    const cuotasSeleccionadasAdelanto = cuotasPendientesAdelanto.slice(0, cantidadSeleccionadaAdelanto);
    const montoAdelanto = cuotasSeleccionadasAdelanto.reduce((sum, cuota) => sum + Number(cuota.monto || 0), 0);

    return (
        <div className="flex h-screen">
            <Sidebar />
            <div className="flex-1 bg-background-light dark:bg-background-dark overflow-auto">
                <main className="p-8">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                                Planes de Pago
                            </h1>
                            <p className="text-slate-500 dark:text-slate-400 mt-1">
                                Gestión de planes de pago consolidados
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/administrativo/planes-pago/nuevo')}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text white rounded-lg hover:bg-primary-hover transition-colors"
                        >
                            <span className="material-symbols-outlined">add</span>
                            Nuevo Plan de Pago
                        </button>
                    </div>

                    {/* Tabla de Planes */}
                    <div className="bg-white dark:bg-[#101c22] rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-200 dark:border-slate-700">
                                        <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            Cliente
                                        </th>
                                        <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            Fecha Inicio
                                        </th>
                                        <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            Cuotas
                                        </th>
                                        <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            Monto Total
                                        </th>
                                        <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            Interés
                                        </th>
                                        <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            Estado
                                        </th>
                                        <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                                                Cargando planes...
                                            </td>
                                        </tr>
                                    ) : planes.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                                                No hay planes de pago registrados
                                            </td>
                                        </tr>
                                    ) : (
                                        planes.map((plan) => (
                                            <tr
                                                key={plan.grupoId}
                                                className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col">
                                                        <span className="font-medium text-slate-900 dark:text-white">
                                                            {plan.cliente.nombre}
                                                        </span>
                                                        <span className="text-sm text-slate-500 dark:text-slate-400">
                                                            Nº {plan.cliente.nroCliente}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                    {formatearFecha(plan.fechaCreacion)}
                                                </td>
                                                <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                    {plan.cantidadCuotas}
                                                </td>
                                                <td className="px-6 py-4 font-semibold text-slate-900 dark:text-white">
                                                    ${formatearMonto(plan.montoTotal)}
                                                </td>
                                                <td className="px-6 py-4 text-slate-700 dark:text-slate-300">
                                                    {plan.interesPorcentaje}%
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${plan.estado === 'activo'
                                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                                                            }`}
                                                    >
                                                        {plan.estado === 'activo' ? 'Activo' : 'Finalizado'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {plan.estado === 'activo' && (
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                onClick={() => abrirModalAdelanto(plan)}
                                                                disabled={registrandoAdelanto}
                                                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary border border-primary/40 rounded-lg hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <span className="material-symbols-outlined text-sm">payments</span>
                                                                Adelantar cuotas
                                                            </button>
                                                            <button
                                                                onClick={() => abrirConfirmRevertir(plan)}
                                                                disabled={revirtiendo}
                                                                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <span className="material-symbols-outlined text-sm">undo</span>
                                                                Revertir
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </main>
            </div>

            {/* Modal Nuevo Plan */}
            {modalNuevoPlan && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-[#101c22] rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                                Nuevo Plan de Pago
                            </h3>
                            <button
                                onClick={cerrarModalNuevoPlan}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Selector de Cliente */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                    Cliente
                                </label>
                                <select
                                    value={clienteSeleccionado?.id || ''}
                                    onChange={handleClienteChange}
                                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    <option value="">Seleccionar cliente...</option>
                                    {clientes.map((cliente) => (
                                        <option key={cliente.id} value={cliente.id}>
                                            {cliente.nombre} (Nº {cliente.nro_cliente})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Deuda del cliente */}
                            {clienteSeleccionado && deudaCliente && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                                        Deuda Actual
                                    </h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-blue-700 dark:text-blue-400">Boletas adeudadas:</span>
                                            <span className="font-semibold text-blue-900 dark:text-blue-200">
                                                {deudaCliente.boletas?.length || 0}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-blue-700 dark:text-blue-400">Total deuda:</span>
                                            <span className="font-bold text-lg text-blue-900 dark:text-blue-100">
                                                ${formatearMonto(deudaCliente.total)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Configuración del Plan */}
                            {clienteSeleccionado && deudaCliente && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Tasa de Interés */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Tasa de Interés (%)
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.1"
                                                value={interes}
                                                onChange={(e) => setInteres(e.target.value)}
                                                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                            />
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                Default: {planConfig.interesPorcentaje}%
                                            </p>
                                        </div>

                                        {/* Cantidad de Cuotas */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Cantidad de Cuotas
                                            </label>
                                            <select
                                                value={cuotas}
                                                onChange={(e) => setCuotas(Number(e.target.value))}
                                                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                            >
                                                {Array.from({ length: planConfig.maxCuotas || 5 }, (_, i) => i + 1).map((n) => (
                                                    <option key={n} value={n}>
                                                        {n} {n === 1 ? 'cuota' : 'cuotas'}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Preview del Plan */}
                                    <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
                                            Vista Previa del Plan
                                        </h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-slate-600 dark:text-slate-400">Deuda base:</span>
                                                <span className="font-medium text-slate-900 dark:text-white">
                                                    ${formatearMonto(montoBase)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-slate-600 dark:text-slate-400">
                                                    Interés ({interesPorc}%):
                                                </span>
                                                <span className="font-medium text-slate-900 dark:text-white">
                                                    ${formatearMonto(montoInteres)}
                                                </span>
                                            </div>
                                            <div className="border-t border-slate-300 dark:border-slate-600 pt-2 flex justify-between">
                                                <span className="font-semibold text-slate-900 dark:text-white">Total del plan:</span>
                                                <span className="font-bold text-lg text-primary">${formatearMonto(montoTotal)}</span>
                                            </div>
                                            <div className="flex justify-between mt-3 pt-3 border-t border-slate-300 dark:border-slate-600">
                                                <span className="text-slate-600 dark:text-slate-400">Cuota mensual:</span>
                                                <span className="font-semibold text-slate-900 dark:text-white">
                                                    ${formatearMonto(montoCuota)}
                                                </span>
                                            </div>
                                            {Math.abs(montoCuotaUltima - montoCuota) > 0.01 && (
                                                <div className="flex justify-between">
                                                    <span className="text-slate-600 dark:text-slate-400">Última cuota:</span>
                                                    <span className="font-semibold text-slate-900 dark:text-white">
                                                        ${formatearMonto(montoCuotaUltima)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Botones */}
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={cerrarModalNuevoPlan}
                                className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                disabled={creandoPlan}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={crearPlan}
                                className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                                disabled={!clienteSeleccionado || !deudaCliente || creandoPlan}
                            >
                                {creandoPlan ? 'Creando...' : 'Crear Plan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Adelantar Cuotas */}
            {modalAdelanto.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-[#101c22] rounded-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                                    Adelantar cuotas
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                    {modalAdelanto.plan?.cliente?.nombre} • Nº {modalAdelanto.plan?.cliente?.nroCliente}
                                </p>
                            </div>
                            <button
                                onClick={cerrarModalAdelanto}
                                disabled={registrandoAdelanto}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>

                        {modalAdelanto.loading ? (
                            <div className="py-12 text-center text-slate-500 dark:text-slate-400">
                                Cargando cuotas del plan...
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                                        <p className="text-xs uppercase text-slate-500 dark:text-slate-400">Pendientes</p>
                                        <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                            {modalAdelanto.preview?.resumen?.cuotasPendientes || 0}
                                        </p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                                        <p className="text-xs uppercase text-slate-500 dark:text-slate-400">Pagadas</p>
                                        <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                            {modalAdelanto.preview?.resumen?.cuotasPagadas || 0}
                                        </p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                                        <p className="text-xs uppercase text-slate-500 dark:text-slate-400">Adelantadas</p>
                                        <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                            {modalAdelanto.preview?.resumen?.cuotasAdelantadas || 0}
                                        </p>
                                    </div>
                                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                                        <p className="text-xs uppercase text-slate-500 dark:text-slate-400">Saldo pendiente</p>
                                        <p className="text-2xl font-bold text-primary">
                                            ${formatearMonto(modalAdelanto.preview?.resumen?.montoPendiente)}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                        Operación
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setModoAdelanto('cuotas')}
                                            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-semibold transition-colors ${modoAdelanto === 'cuotas'
                                                ? 'bg-primary text-white border-primary'
                                                : 'border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-primary'
                                                }`}
                                        >
                                            <span className="material-symbols-outlined text-lg">playlist_add_check</span>
                                            Adelantar cuotas
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setModoAdelanto('total')}
                                            className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-semibold transition-colors ${modoAdelanto === 'total'
                                                ? 'bg-primary text-white border-primary'
                                                : 'border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-primary'
                                                }`}
                                        >
                                            <span className="material-symbols-outlined text-lg">done_all</span>
                                            Pagar total pendiente
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {modoAdelanto === 'cuotas' && (
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Cantidad de cuotas
                                            </label>
                                            <input
                                                type="number"
                                                min="1"
                                                max={cuotasPendientesAdelanto.length}
                                                value={cantidadAdelanto}
                                                onChange={(e) => setCantidadAdelanto(Number(e.target.value))}
                                                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Fecha de pago
                                        </label>
                                        <input
                                            type="date"
                                            value={fechaAdelanto}
                                            onChange={(e) => setFechaAdelanto(e.target.value)}
                                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                            Medio de pago
                                        </label>
                                        <select
                                            value={metodoAdelanto}
                                            onChange={(e) => setMetodoAdelanto(e.target.value)}
                                            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                        >
                                            <option value="efectivo">Efectivo</option>
                                            <option value="transferencia">Transferencia</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div>
                                            <p className="text-sm font-semibold text-slate-900 dark:text-white">Cuotas a adelantar</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                Se toman siempre las próximas cuotas pendientes.
                                            </p>
                                        </div>
                                        <p className="text-xl font-bold text-primary">${formatearMonto(montoAdelanto)}</p>
                                    </div>

                                    {cuotasSeleccionadasAdelanto.length === 0 ? (
                                        <p className="text-sm text-slate-500 dark:text-slate-400">No hay cuotas pendientes para adelantar.</p>
                                    ) : (
                                        <div className="max-h-48 overflow-y-auto divide-y divide-slate-200 dark:divide-slate-700">
                                            {cuotasSeleccionadasAdelanto.map((cuota) => (
                                                <div key={cuota.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                                                    <div>
                                                        <p className="font-medium text-slate-900 dark:text-white">
                                                            Cuota {cuota.numero}/{cuota.totalCuotas}
                                                        </p>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                                            {cuota.mes}/{cuota.anio}
                                                        </p>
                                                    </div>
                                                    <span className="font-semibold text-slate-900 dark:text-white">
                                                        ${formatearMonto(cuota.monto)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                        Observaciones
                                    </label>
                                    <textarea
                                        rows={3}
                                        value={observacionesAdelanto}
                                        onChange={(e) => setObservacionesAdelanto(e.target.value)}
                                        placeholder="Ej: Cliente abonó cuotas por ventanilla"
                                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                                    />
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                    <button
                                        onClick={cerrarModalAdelanto}
                                        disabled={registrandoAdelanto}
                                        className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={confirmarAdelanto}
                                        disabled={registrandoAdelanto || cuotasSeleccionadasAdelanto.length === 0}
                                        className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    >
                                        {registrandoAdelanto ? 'Registrando...' : `Registrar pago de $${formatearMonto(montoAdelanto)}`}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal Confirmar Revertir */}
            {modalConfirmRevertir.isOpen && (
                <Modal
                    isOpen={modalConfirmRevertir.isOpen}
                    onClose={() => setModalConfirmRevertir({ isOpen: false, grupoId: null, clienteNombre: '' })}
                    type="warning"
                    title="Revertir Plan de Pago"
                    message={`¿Estás seguro de revertir el plan de ${modalConfirmRevertir.clienteNombre}? Las boletas volverán a estado pendiente y se eliminarán los cargos de cuotas. Esta acción no se puede deshacer.`}
                    confirmText="Sí, revertir"
                    onConfirm={confirmarRevertir}
                    cancelText="Cancelar"
                    onCancel={() => setModalConfirmRevertir({ isOpen: false, grupoId: null, clienteNombre: '' })}
                />
            )}

            {/* Modal de Mensajes */}
            {modal.isOpen && (
                <Modal
                    isOpen={modal.isOpen}
                    onClose={closeModal}
                    type={modal.type}
                    title={modal.title}
                    message={modal.message}
                />
            )}
        </div>
    );
};

export default PlanesAdminPage;
